import React, { useState, useEffect, useRef } from 'react';
import {
  Input, Button, Space, Switch, Modal,
  Tag, Select, notification, Card, Tooltip, Dropdown, Menu
} from 'antd';
import {
  PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined,
  ReloadOutlined, MoreOutlined, ExclamationCircleOutlined
} from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import productService from '../../../services/productService';
import categoryService from '../../../services/categoryService';
import config from '../../../config';
import { AddProductModal, EditProductModal, ViewProductModal } from './modals';
import userService from '../../../services/userService';
import usePermission from '../../../hooks/usePermission';
import MyTable from '../../../components/common/MyTable';
import { emptySchedoxCatalog, normalizeSchedoxCatalog } from '../../../utils/schedoxCatalog';

const { Search } = Input;
const { Option } = Select;
const { confirm } = Modal;

const resolveAssetUrl = (value) => {
  if (!value) return '';
  const cleanValue = String(value).replace(/^(\.\.\/)+/, '').replace(/^\/+/, '');
  if (/^https?:\/\//i.test(cleanValue)) return cleanValue;
  return `${config.APP_URL}/${cleanValue}`;
};

const ProductList = () => {
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryTypes, setCategoryTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [users, setUsers] = useState([]);
  const [schedoxCatalog, setSchedoxCatalog] = useState(emptySchedoxCatalog);
  const [schedoxCatalogLoading, setSchedoxCatalogLoading] = useState(true);
  const { hasPermission } = usePermission();
  const [filters, setFilters] = useState({
    category: location.state?.categoryFilter || null,
    status: null,
    type: null
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [sortInfo, setSortInfo] = useState({
    columnKey: null,
    order: null
  });
  
  // Modal states
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  
  const searchInputRef = useRef(null);

  useEffect(() => {
    fetchUsers();
    fetchCategories();
    fetchSchedoxCatalog();
  }, []);

  useEffect(() => {
    if (location.state?.categoryFilter && location.state.categoryFilter !== filters.category) {
      setFilters(prev => ({ ...prev, category: location.state.categoryFilter }));
      setPagination(prev => ({ ...prev, current: 1 }));
    }
  }, [location.state?.categoryFilter, filters.category]);

  useEffect(() => {
    if (searchText.length === 1) return;
    fetchProducts();
  }, [filters, pagination.current, pagination.pageSize, sortInfo, searchText]);

  const fetchUsers = async () => {
    try {
      const params = { limit: 300 };
      const response = await userService.getAllCustomers(params);
      setUsers(response.data || []);
    } catch (error) {
      notification.error({
        message: 'Error fetching users',
        description: error.message || 'Failed to load users'
      });
    }
  };
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        user_id: filters.user,
        search: searchText,
        category_id: filters.category,
        status: filters.status,
        category_type_id: filters.type,
        sort_field: sortInfo.columnKey,
        sort_order: sortInfo.order
      };
      
      const response = await productService.getAllProducts(params);
      const productPayload = response?.data || {};
      setProducts(productPayload.data || []);
      setPagination(prev => ({
        ...prev,
        current: productPayload.current_page || params.page || prev.current,
        pageSize: Number(productPayload.per_page || params.limit || prev.pageSize),
        total: productPayload.total || 0
      }));
    } catch (error) {
      notification.error({
        message: 'Error fetching products',
        description: error.message || 'Failed to load products'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedoxCatalog = async () => {
    try {
      setSchedoxCatalogLoading(true);
      const response = await productService.getSchedoxCatalog();
      setSchedoxCatalog(normalizeSchedoxCatalog(response?.data));
    } catch (error) {
      notification.error({
        message: 'Error fetching Schedox services',
        description: error.message || 'Failed to load the Schedox service catalogue'
      });
    } finally {
      setSchedoxCatalogLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoryService.getAllCategories();
      const categoryPayload = response?.data?.data || response?.data || [];
      setCategories(Array.isArray(categoryPayload) ? categoryPayload : categoryPayload.data || []);

      const typeResponse = await categoryService.getCategoryTypes();
      setCategoryTypes(typeResponse?.data || []);
    } catch (error) {
      notification.error({
        message: 'Error fetching categories',
        description: error.message || 'Failed to load categories'
      });
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setPagination(pagination);
    
    if (sorter && sorter.columnKey && sorter.order) {
      setSortInfo({
        columnKey: sorter.columnKey,
        order: sorter.order === 'ascend' ? 'asc' : 'desc'
      });
    } else {
      setSortInfo({ columnKey: null, order: null });
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleResetFilters = () => {
    setFilters({ category: null, status: null, type: null });
    setSearchText('');
    setSortInfo({ columnKey: null, order: null });
    setPagination(prev => ({ ...prev, current: 1 }));
    if (searchInputRef.current) {
      searchInputRef.current.input.value = '';
    }
  };

  const showDeleteConfirm = (productId) => {
    confirm({
      title: 'Are you sure you want to delete this product?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        handleDelete(productId);
      }
    });
  };

  const handleDelete = async (productId) => {
    try {
      await productService.deleteProduct(productId);
      notification.success({
        message: 'Product deleted successfully'
      });
      fetchProducts();
    } catch (error) {
      notification.error({
        message: 'Error deleting product',
        description: error.message || 'Failed to delete product'
      });
    }
  };

  const handleStatusChange = async (checked, productId) => {
    try {
      await productService.updateProductStatus(productId, checked ? 1 : 0);
      notification.success({
        message: `Product ${checked ? 'activated' : 'deactivated'} successfully`
      });
      
      // Update the product status in the current list without refetching
      setProducts(prevProducts => 
        prevProducts.map(product => 
          product.id === productId 
            ? { ...product, status: checked ? '1' : '0' } 
            : product
        )
      );
    } catch (error) {
      notification.error({
        message: 'Error updating product status',
        description: error.message || 'Failed to update product status'
      });
    }
  };

  // Modal handlers
  const showAddModal = () => {
    setAddModalVisible(true);
  };

  const showEditModal = (productId) => {
    setSelectedProductId(productId);
    setEditModalVisible(true);
  };

  const showViewModal = (productId) => {
    setSelectedProductId(productId);
    setViewModalVisible(true);
  };

  const handleModalSuccess = () => {
    fetchProducts();
    setAddModalVisible(false);
    setEditModalVisible(false);
  };

  const closeModals = () => {
    setAddModalVisible(false);
    setEditModalVisible(false);
    setViewModalVisible(false);
    setSelectedProductId(null);
  };

  const formatPrice = (price) => {
    return parseFloat(price || 0).toFixed(2);
  };

  const getStockStatusTag = (quantity) => {
    const qty = parseInt(quantity);
    if (qty <= 0) {
      return <Tag color="red">Out of Stock</Tag>;
    } else if (qty <= 10) {
      return <Tag color="orange">Low Stock ({qty})</Tag>;
    } else {
      return <Tag color="green">In Stock ({qty})</Tag>;
    }
  };

  const renderCategoryTag = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return categoryId;
    
    const categoryType = categoryTypes.find(t => t.id === category.category_type_id);
    const typeLabel = categoryType ? ` (${categoryType.category_type})` : '';
    
    return (
      <Tag color="blue">
        {category.category_title}{typeLabel}
      </Tag>
    );
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: true,
      width: 80,
      fixed: 'left'
    },
    {
      title: 'Image',
      dataIndex: 'image',
      key: 'image',
      width: 100,
      render: (image, record) => (
        <div className="w-16 h-16 flex-shrink-0">
          {image ? (
            <img 
              src={resolveAssetUrl(record.image_url || image)}
              alt={record.product_title} 
              className="w-full h-full object-cover rounded border border-gray-200"
              onError={(e) => { e.target.src = '/logo.png'; }}
            />
          ) : (
            <div className="w-full h-full bg-gray-200 rounded flex items-center justify-center text-gray-500">
              No Image
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Product Title',
      dataIndex: 'product_title',
      key: 'product_title',
      render: (text, record) => (
        <div>
          <div className="font-medium">{text}</div>
          <div className="text-xs text-gray-500">
            Code: {record.product_code || 'N/A'}
            {record.is_pack === 1 && <Tag color="purple" className="ml-2">Pack</Tag>}
            {record.sync_to_schedox ? <Tag color="green" className="ml-2">Schedox</Tag> : null}
            {record.hidden_from_client_portal ? <Tag color="orange" className="ml-2">Portal hidden</Tag> : null}
          </div>
          {record.product_description && (
            <div className="text-xs italic text-gray-500 mt-1 max-w-xs truncate">
              {record.product_description}
            </div>
          )}
        </div>
      ),
      sorter: true,
      ellipsis: true
    },
    {
      title: 'Category',
      dataIndex: 'category_id',
      key: 'category_id',
      render: (categoryId) => renderCategoryTag(categoryId),
      filters: categories.map(category => ({ text: category.category_title, value: category.id })),
      filtered: filters.category !== null,
      filterMultiple: false,
      width: 180
    },
    {
      title: 'Price',
      key: 'price',
      render: (_, record) => {
        const isRental = record.priceone > 0 || record.pricetwo > 0 || record.pricethree > 0 || record.pricefour > 0;
        
        if (isRental) {
          return (
            <Tooltip title="Rental pricing" placement="topLeft">
              <div>
                <div className="text-xs">3 Days: £{formatPrice(record.priceone)}</div>
                <div className="text-xs">1 Week: £{formatPrice(record.pricetwo)}</div>
                <div className="text-xs">2 Weeks: £{formatPrice(record.pricethree)}</div>
                <div className="text-xs">1 Month: £{formatPrice(record.pricefour)}</div>
              </div>
            </Tooltip>
          );
        }
        
        return `£${formatPrice(record.product_price)}`;
      },
      sorter: true,
      width: 120
    },
    {
      title: 'Stock',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (quantity) => getStockStatusTag(quantity),
      sorter: true,
      width: 120
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => (
        <Switch 
          checked={status === 1 || status === '1'} 
          onChange={(checked) => handleStatusChange(checked, record.id)} 
        />
      ),
      filters: [
        { text: 'Active', value: '1' },
        { text: 'Inactive', value: '0' }
      ],
      filtered: filters.status !== null,
      filterMultiple: false,
      width: 100
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => showViewModal(record.id)}
              className="text-blue-500 hover:text-blue-700"
            />
          </Tooltip>
          {hasPermission('edit_products') &&   <Tooltip title="Edit Product">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => showEditModal(record.id)}
              className="text-yellow-500 hover:text-yellow-700"
            />
          </Tooltip>}
          {hasPermission('delete_products') &&   <Tooltip title="Delete Product">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => showDeleteConfirm(record.id)}
              className="text-red-500 hover:text-red-700"
            />
          </Tooltip>}
        </Space>
      ),
      width: 120,
      fixed: 'right'
    }
  ];

  const actionsMenu = (
    <Menu>
     
      
    </Menu>
  );

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
        <h1 className="text-2xl font-bold">Products Management</h1>
        <Space wrap>
        {hasPermission('create_products') &&  <Button
            type="primary"
            onClick={showAddModal}
            icon={<PlusOutlined />}
            className="bg-golden-navy"
          >
            Add Product
          </Button>}
          <Dropdown overlay={actionsMenu}>
            <Button icon={<MoreOutlined />}>Actions</Button>
          </Dropdown>
        </Space>
      </div>

      <Card className="shadow-md mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div className="flex flex-wrap items-center gap-4">
            <Search
              placeholder="Search products..."
              allowClear
              onSearch={handleSearch}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ width: 250 }}
              ref={searchInputRef}
            />
            
            <Select 
              placeholder="Filter by category" 
              style={{ width: 200 }}
              allowClear
              value={filters.category}
              onChange={(value) => handleFilterChange('category', value)}
            >
              {categories.map(category => (
                <Option key={category.id} value={category.id}>{category.category_title}</Option>
              ))}
            </Select>

            <Select 
              placeholder="Filter by type" 
              style={{ width: 150 }}
              allowClear
              value={filters.type}
              onChange={(value) => handleFilterChange('type', value)}
            >
              {categoryTypes.map(type => (
                <Option key={type.id} value={type.id}>{type.category_type}</Option>
              ))}
            </Select>

            <Select 
              placeholder="Filter by status" 
              style={{ width: 150 }}
              allowClear
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
            >
              <Option value="1">Active</Option>
              <Option value="0">Inactive</Option>
            </Select>

            <Button 
              icon={<ReloadOutlined />} 
              onClick={handleResetFilters}
              className="flex items-center"
            >
              Reset
            </Button>
          </div>

          <div className="flex gap-2">
            
            <Button
              type="primary"
              onClick={fetchProducts}
              icon={<ReloadOutlined />}
              className="bg-golden-navy"
            >
              Refresh
            </Button>
          </div>
        </div>

        <div className="space-y-3 sm:hidden">
          {products.map(product => (
            <article key={`mobile-${product.id}`} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex gap-3">
                <img
                  src={resolveAssetUrl(product.image_url || product.image) || '/logo.png'}
                  alt=""
                  className="h-16 w-16 shrink-0 rounded-lg border border-gray-100 object-cover"
                  onError={(event) => { event.target.src = '/logo.png'; }}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h2 className="font-semibold leading-5 text-golden-navy">{product.product_title}</h2>
                      <p className="mt-1 text-xs text-gray-500">{product.product_code || `Product #${product.id}`}</p>
                    </div>
                    <Switch
                      aria-label={`Set ${product.product_title} active status`}
                      checked={product.status === 1 || product.status === '1'}
                      onChange={(checked) => handleStatusChange(checked, product.id)}
                    />
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {renderCategoryTag(product.category_id)}
                    {getStockStatusTag(product.quantity)}
                    <strong className="text-sm text-golden-navy">£{formatPrice(product.product_price)}</strong>
                  </div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 border-t border-gray-100 pt-3">
                <Button icon={<EyeOutlined />} onClick={() => showViewModal(product.id)}>View</Button>
                <Button disabled={!hasPermission('edit_products')} icon={<EditOutlined />} onClick={() => showEditModal(product.id)}>Edit</Button>
                <Button disabled={!hasPermission('delete_products')} danger icon={<DeleteOutlined />} onClick={() => showDeleteConfirm(product.id)}>Delete</Button>
              </div>
            </article>
          ))}
          {!loading && products.length === 0 ? <p className="py-8 text-center text-gray-500">No products found.</p> : null}
        </div>

        <div className="hidden sm:block">
          <MyTable
            columns={columns}
            dataSource={products}
            rowKey="id"
            pagination={pagination}
            loading={loading}
            handleTableChange={handleTableChange}
            scroll={{ x: 1200 }}
            size="middle"
          />
        </div>

        {pagination.total > 0 ? (
          <div className="mt-4 flex flex-col gap-3 sm:hidden">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>{((pagination.current - 1) * pagination.pageSize) + 1}-{Math.min(pagination.current * pagination.pageSize, pagination.total)} of {pagination.total}</span>
              <Select
                value={pagination.pageSize >= pagination.total ? 'all' : pagination.pageSize}
                onChange={(value) => handleTableChange({ ...pagination, current: 1, pageSize: value === 'all' ? pagination.total : Number(value) }, {}, {})}
                style={{ width: 120 }}
              >
                {[10, 25, 50, 100].filter(value => value <= pagination.total || value === 10).map(value => (
                  <Option key={value} value={value}>{value} / page</Option>
                ))}
                <Option value="all">All</Option>
              </Select>
            </div>
            {Math.ceil(pagination.total / pagination.pageSize) > 1 ? (
              <div className="grid grid-cols-3 items-center gap-2">
                <Button disabled={pagination.current <= 1} onClick={() => handleTableChange({ ...pagination, current: pagination.current - 1 }, {}, {})}>Previous</Button>
                <span className="text-center text-sm">Page {pagination.current} of {Math.ceil(pagination.total / pagination.pageSize)}</span>
                <Button disabled={pagination.current >= Math.ceil(pagination.total / pagination.pageSize)} onClick={() => handleTableChange({ ...pagination, current: pagination.current + 1 }, {}, {})}>Next</Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </Card>

      {/* Modals */}
      <AddProductModal 
        visible={addModalVisible}
        schedoxCatalog={schedoxCatalog}
        schedoxCatalogLoading={schedoxCatalogLoading}
        onCancel={closeModals}
        onSuccess={handleModalSuccess}
      />

      <EditProductModal 
        visible={editModalVisible}
        userso={users}
        schedoxCatalog={schedoxCatalog}
        schedoxCatalogLoading={schedoxCatalogLoading}
        productId={selectedProductId}
        onCancel={closeModals}
        onSuccess={handleModalSuccess}
      />

      <ViewProductModal 
        visible={viewModalVisible}
      users_org={users}
        productId={selectedProductId}
        onCancel={closeModals}
        users={users}
        schedoxCatalog={schedoxCatalog}
      />
    </div>
  );
};

export default ProductList;
